// BILLING-PLANS: the parts Operator › Billing's quick-action modals share — a dialog with its
// form and footer, a dollars field read as exact integer cents, a percent field read as basis
// points, a plain text/number/date field and a select. Explanations sit behind (i), not under
// the field. API problems are toasted by the query client (api/problem-toast.ts).
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import { type ComponentProps, type ReactNode, useId } from "react";
import { parseDollarsToCents, parsePercentToBp } from "../../billing/money";

export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  pending,
  canSubmit,
  submitLabel,
  onSubmit,
  secondary,
  wide = false,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  pending: boolean;
  canSubmit: boolean;
  submitLabel: string;
  onSubmit: () => void;
  /** Another submit (Save draft beside Send), rendered before the primary. */
  secondary?: { label: string; onClick: () => void };
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => (pending ? null : onOpenChange(next))}>
      <DialogContent className={wide ? "sm:max-w-2xl" : "sm:max-w-lg"}>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !pending) onSubmit();
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : (
              <DialogDescription className="sr-only">{title}</DialogDescription>
            )}
          </DialogHeader>
          {children}
          <DialogFooter>
            <DialogClose disabled={pending} render={<Button variant="secondary" type="button" />}>
              Cancel
            </DialogClose>
            {secondary ? (
              <Button
                type="button"
                variant="secondary"
                disabled={!canSubmit || pending}
                onClick={secondary.onClick}
              >
                {secondary.label}
              </Button>
            ) : null}
            <Button type="submit" disabled={!canSubmit || pending}>
              {pending ? "Saving…" : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Label({ id, label, info }: { id: string; label: string; info?: ReactNode }) {
  return (
    <span className="flex items-center gap-1">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {info ? <InfoTip label={`About ${label}`}>{info}</InfoTip> : null}
    </span>
  );
}

/** A dollars field; `optional` lets it be blank (= the default / no override). */
export function MoneyField({
  label,
  value,
  onChange,
  info,
  optional = false,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  info?: ReactNode;
  optional?: boolean;
  placeholder?: string;
}) {
  const id = useId();
  const bad = value.trim() !== "" && parseDollarsToCents(value) === null;
  return (
    <Field>
      <Label id={id} label={label} info={info} />
      <Input
        id={id}
        inputMode="decimal"
        autoComplete="off"
        value={value}
        placeholder={placeholder ?? (optional ? "Default" : "0.00")}
        required={!optional}
        aria-invalid={bad ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

/** A percent field (two decimals at most). */
export function PercentField({
  label,
  value,
  onChange,
  info,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  info?: ReactNode;
}) {
  const id = useId();
  const bad = value.trim() !== "" && parsePercentToBp(value) === null;
  return (
    <Field>
      <Label id={id} label={`${label} (%)`} info={info} />
      <Input
        id={id}
        inputMode="decimal"
        autoComplete="off"
        value={value}
        required
        aria-invalid={bad ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function TextField({
  label,
  value,
  onChange,
  info,
  ...props
}: Omit<ComponentProps<typeof Input>, "value" | "onChange" | "id"> & {
  label: string;
  value: string;
  onChange: (v: string) => void;
  info?: ReactNode;
}) {
  const id = useId();
  return (
    <Field>
      <Label id={id} label={label} info={info} />
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} {...props} />
    </Field>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  info,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: readonly { value: T; label: string }[];
  info?: ReactNode;
}) {
  const id = useId();
  return (
    <Field>
      <Label id={id} label={label} info={info} />
      <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <NativeSelectOption key={o.value} value={o.value}>
            {o.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Field>
  );
}

/** Blank → null; else the integer, or undefined when it is not one (invalid). */
export function optionalInt(text: string): number | null | undefined {
  const s = text.trim();
  if (s === "") return null;
  return /^\d+$/.test(s) ? Number(s) : undefined;
}

/** Blank → null; else exact cents, or undefined when invalid. */
export function optionalCents(text: string): number | null | undefined {
  if (text.trim() === "") return null;
  return parseDollarsToCents(text) ?? undefined;
}

export const todayIso = () => new Date().toISOString().slice(0, 10);
export const addDaysIso = (days: number) =>
  new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
